import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { BarChart3, Download, Calendar, Users, BookOpen, ChevronRight, TrendingUp } from 'lucide-react';
import { cn } from '../../lib/utils';
import getLoansList from '../../api/GetLoansList';

const MONTHS_ORDER = [
    { index: 7, name: 'Août' },
    { index: 8, name: 'Septembre' },
    { index: 9, name: 'Octobre' },
    { index: 10, name: 'Novembre' },
    { index: 11, name: 'Décembre' },
    { index: 0, name: 'Janvier' },
    { index: 1, name: 'Février' },
    { index: 2, name: 'Mars' },
    { index: 3, name: 'Avril' },
    { index: 4, name: 'Mai' },
    { index: 5, name: 'Juin' },
    { index: 6, name: 'Juillet' }
];

export default function LoanStatsBySchoolYear() {
    const [loans, setLoans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedYear, setSelectedYear] = useState(null);

    const currentUser = useSelector((state) => state.user.currentUser);
    const API_BASE = useSelector((state) => state.user.apiBase);
    const token = useSelector((state) => state.user.currentUser.token);
    const code_etablissement = useSelector((state) => state.user.currentUser.code_etablissement);
    const { items: books } = useSelector((state) => state.books);
    const { items: members } = useSelector((state) => state.members);

    useEffect(() => {
        const fetchLoansData = async () => {
            setLoading(true);
            setError(null);
            try {
                let allLoans = await getLoansList(API_BASE, token, code_etablissement);

                // Scope to student's own loans if student
                if (currentUser.role === 'Étudiant') {
                    const myId = currentUser.id || currentUser.ID;
                    allLoans = allLoans.filter(loan => loan.user_id == myId);
                }

                setLoans(allLoans);

                // Determine default selected year (most recent)
                const processed = processLoansData(allLoans);
                if (processed.length > 0) {
                    // Sort years descending to get the latest one
                    const sortedYears = [...processed].sort((a, b) => b.schoolYear.localeCompare(a.schoolYear));
                    setSelectedYear(sortedYears[0].schoolYear);
                }
            } catch (err) {
                console.error("Failed to fetch loans for school year stats:", err);
                setError("Impossible de charger les statistiques des emprunts.");
            } finally {
                setLoading(false);
            }
        };

        if (API_BASE && token && code_etablissement) {
            fetchLoansData();
        }
    }, [API_BASE, token, code_etablissement, currentUser, books, members]);

    const processLoansData = (loansList) => {
        const yearsMap = {};

        loansList.forEach(loan => {
            if (!loan.start_date || loan.start_date.startsWith("0001")) return;

            const date = new Date(loan.start_date);
            if (isNaN(date.getTime())) return;

            const year = date.getFullYear();
            const month = date.getMonth(); // 0-11

            // Aug (7) to Jul (6)
            let sy;
            if (month >= 7) {
                sy = `${year}-${year + 1}`;
            } else {
                sy = `${year - 1}-${year}`;
            }

            if (!yearsMap[sy]) {
                yearsMap[sy] = {
                    schoolYear: sy,
                    total: 0,
                    months: {
                        'Août': 0, 'Septembre': 0, 'Octobre': 0, 'Novembre': 0, 'Décembre': 0,
                        'Janvier': 0, 'Février': 0, 'Mars': 0, 'Avril': 0, 'Mai': 0, 'Juin': 0, 'Juillet': 0
                    },
                    roles: {},
                    categories: {},
                    loansList: []
                };
            }

            yearsMap[sy].total++;
            yearsMap[sy].loansList.push(loan);

            // Month breakdown
            const monthName = MONTHS_ORDER.find(item => item.index === month)?.name;
            if (monthName) {
                yearsMap[sy].months[monthName]++;
            }

            // Role breakdown
            const member = members.find(m => (m.ID || m.id) == loan.user_id);
            const role = member?.role || member?.Role || "Autre";
            yearsMap[sy].roles[role] = (yearsMap[sy].roles[role] || 0) + 1;

            // Category breakdown
            const book = books.find(b => (b.item_id || b.id) == loan.item_id);
            const category = book?.category || "Non classifié";
            yearsMap[sy].categories[category] = (yearsMap[sy].categories[category] || 0) + 1;
        });

        // Convert map to array and sort chronologically (e.g. 2023-2024, 2024-2025)
        return Object.values(yearsMap).sort((a, b) => a.schoolYear.localeCompare(b.schoolYear));
    };

    const processedData = processLoansData(loans);
    const maxTotal = processedData.length > 0 ? Math.max(...processedData.map(d => d.total)) : 0;
    const selectedYearData = processedData.find(d => d.schoolYear === selectedYear);

    const handleExportCSV = () => {
        if (!selectedYearData) return;

        const headers = ["ID Emprunt", "Titre du Livre", "Auteur", "Catégorie", "Emprunteur", "Rôle", "Date d'emprunt", "Date de retour"];
        const rows = selectedYearData.loansList.map(loan => {
            const member = members.find(m => (m.ID || m.id) == loan.user_id);
            const book = books.find(b => (b.item_id || b.id) == loan.item_id);
            const returnDate = loan.end_date && !loan.end_date.startsWith("0001")
                ? new Date(loan.end_date).toLocaleDateString('fr-FR')
                : "En cours";
            return [
                loan.id || loan.ID || "",
                book?.title || loan.item?.manifestation?.title || "Livre inconnu",
                book?.author || "Auteur inconnu",
                book?.category || "Non classifié",
                member?.name || "Utilisateur inconnu",
                member?.role || member?.Role || "Autre",
                new Date(loan.start_date).toLocaleDateString('fr-FR'),
                returnDate
            ];
        });

        const csvContent = [
            headers.join(";"),
            ...rows.map(e => e.map(val => `"${String(val).replace(/"/g, '""')}"`).join(";"))
        ].join("\n");

        const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `emprunts_biblius_${selectedYear}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Find role color styling
    const getRoleColor = (roleName) => {
        switch (roleName.toLowerCase()) {
            case 'étudiant':
            case 'eleve':
            case 'élève':
                return 'bg-blue-500';
            case 'enseignant':
            case 'professeur':
                return 'bg-purple-500';
            case 'bibliothécaire':
            case 'admin':
                return 'bg-emerald-500';
            default:
                return 'bg-slate-500';
        }
    };

    if (loading) {
        return (
            <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-6 w-full animate-pulse min-h-[400px] flex flex-col justify-between">
                <div className="h-6 bg-white/10 rounded w-1/3 mb-4"></div>
                <div className="h-48 bg-white/5 rounded-xl w-full flex items-end justify-between p-4 gap-4">
                    <div className="h-24 bg-white/10 rounded w-full"></div>
                    <div className="h-32 bg-white/10 rounded w-full"></div>
                    <div className="h-40 bg-white/10 rounded w-full"></div>
                </div>
                <div className="h-10 bg-white/10 rounded w-1/4 self-end mt-4"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-6 w-full min-h-[250px] flex flex-col items-center justify-center text-center">
                <BarChart3 className="text-red-400 h-12 w-12 mb-3" />
                <p className="text-white font-medium mb-1">{error}</p>
                <p className="text-slate-400 text-sm">Veuillez rafraîchir la page ou réessayer ultérieurement.</p>
            </div>
        );
    }

    if (processedData.length === 0) {
        return (
            <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-6 w-full min-h-[250px] flex flex-col items-center justify-center text-center">
                <BarChart3 className="text-slate-500 h-12 w-12 mb-3" />
                <p className="text-white font-medium mb-1">Aucun emprunt enregistré</p>
                <p className="text-slate-400 text-sm">Les statistiques s'afficheront dès que des livres seront empruntés.</p>
            </div>
        );
    }

    // Process breakdown stats for month distribution
    const maxMonthTotal = selectedYearData
        ? Math.max(...Object.values(selectedYearData.months))
        : 0;

    // Process breakdown stats for categories
    const sortedCategories = selectedYearData
        ? Object.entries(selectedYearData.categories)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5)
        : [];

    return (
        <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-6 w-full space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-primary/20 rounded-xl border border-primary/20 text-primary">
                        <BarChart3 size={22} />
                    </div>
                    <div>
                        <h3 className="text-xl font-bold text-white">
                            {currentUser.role === 'Étudiant' ? 'Mes emprunts par année scolaire' : 'Emprunts par année scolaire'}
                        </h3>
                        <p className="text-slate-400 text-xs mt-0.5">Statistiques regroupées d'août à juillet</p>
                    </div>
                </div>

                {selectedYearData && (
                    <button
                        onClick={handleExportCSV}
                        className="flex items-center justify-center gap-2 px-4 py-2 bg-white/5 border border-white/10 hover:bg-white/10 text-white rounded-xl text-sm font-medium transition-colors self-start sm:self-center"
                    >
                        <Download size={16} />
                        Exporter l'année {selectedYear}
                    </button>
                )}
            </div>

            {/* Layout Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* School Year Chart (Cols 1 & 2) */}
                <div className="lg:col-span-2 flex flex-col justify-between bg-slate-950/20 border border-white/5 rounded-2xl p-5 min-h-[320px]">
                    <div className="flex justify-between items-center mb-6">
                        <span className="text-xs text-slate-400 font-semibold tracking-wider uppercase">Évolution annuelle</span>
                        <div className="flex items-center gap-1.5 text-xs text-emerald-400 bg-emerald-500/10 px-2.5 py-1 rounded-full font-medium">
                            <TrendingUp size={12} />
                            <span>{loans.length} emprunt{loans.length > 1 ? 's' : ''} au total</span>
                        </div>
                    </div>

                    {/* Bars Container */}
                    <div className="h-48 flex items-end justify-around gap-4 border-b border-white/10 pb-3 relative">
                        {processedData.map((data, index) => {
                            const percentage = maxTotal > 0 ? (data.total / maxTotal) * 90 + 10 : 0; // standard scale 10% to 100%
                            const isActive = data.schoolYear === selectedYear;

                            return (
                                <div
                                    key={data.schoolYear}
                                    className="flex flex-col items-center flex-1 group cursor-pointer h-full justify-end"
                                    onClick={() => setSelectedYear(data.schoolYear)}
                                >
                                    <div className="w-full flex flex-col items-center justify-end h-full">
                                        <motion.div
                                            initial={{ height: 0 }}
                                            animate={{ height: `${percentage}%` }}
                                            transition={{ duration: 0.6, delay: index * 0.1, ease: "easeOut" }}
                                            className={cn(
                                                "w-full max-w-[48px] rounded-t-lg transition-all duration-300 relative",
                                                isActive
                                                    ? "bg-gradient-to-t from-primary to-blue-400 shadow-[0_0_20px_rgba(59,130,246,0.35)]"
                                                    : "bg-white/10 group-hover:bg-white/20"
                                            )}
                                        >
                                            {/* Count Badge on Top of Bar */}
                                            <div className={cn(
                                                "absolute -top-7 left-1/2 -translate-x-1/2 text-[10px] font-bold px-1.5 py-0.5 rounded transition-all whitespace-nowrap",
                                                isActive ? "bg-primary text-white scale-110" : "bg-slate-800 text-slate-300 opacity-0 group-hover:opacity-100"
                                            )}>
                                                {data.total}
                                            </div>
                                        </motion.div>
                                    </div>
                                    <span className={cn(
                                        "text-xs mt-3 transition-colors font-medium whitespace-nowrap",
                                        isActive ? "text-primary" : "text-slate-400 group-hover:text-white"
                                    )}>
                                        {data.schoolYear}
                                    </span>
                                </div>
                            );
                        })}
                    </div>

                    <div className="flex justify-between items-center mt-3 text-[11px] text-slate-500">
                        <span>Axe X : Année scolaire (Août - Juillet)</span>
                        <span>Cliquez sur une barre pour analyser les détails</span>
                    </div>
                </div>

                {/* Statistics Breakdown (Col 3) */}
                <div className="flex flex-col space-y-5">
                    <div className="border-b border-white/10 pb-2">
                        <span className="text-xs text-slate-400 font-semibold tracking-wider uppercase">Détails : Année {selectedYear}</span>
                    </div>

                    {selectedYearData ? (
                        <div className="space-y-5 overflow-y-auto max-h-[300px] pr-1">
                            {/* Monthly Breakdown */}
                            <div className="space-y-2.5">
                                <h4 className="text-xs font-semibold text-white flex items-center gap-1.5">
                                    <Calendar size={13} className="text-slate-400" />
                                    Distribution Mensuelle
                                </h4>
                                <div className="space-y-2">
                                    {MONTHS_ORDER.map(month => {
                                        const count = selectedYearData.months[month.name] || 0;
                                        const percentage = maxMonthTotal > 0 ? (count / maxMonthTotal) * 100 : 0;

                                        return (
                                            <div key={month.name} className="flex items-center justify-between text-xs gap-3">
                                                <span className="text-slate-400 w-20 truncate">{month.name}</span>
                                                <div className="flex-1 bg-white/5 h-2 rounded-full overflow-hidden">
                                                    <motion.div
                                                        initial={{ width: 0 }}
                                                        animate={{ width: `${percentage}%` }}
                                                        transition={{ duration: 0.5, ease: "easeOut" }}
                                                        className="h-full bg-primary/60 rounded-full"
                                                    />
                                                </div>
                                                <span className="text-white font-medium w-8 text-right">{count}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Role Breakdown (only if there are roles) */}
                            {Object.keys(selectedYearData.roles).length > 0 && currentUser.role !== 'Étudiant' && (
                                <div className="space-y-2.5 pt-3 border-t border-white/5">
                                    <h4 className="text-xs font-semibold text-white flex items-center gap-1.5">
                                        <Users size={13} className="text-slate-400" />
                                        Répartition par Rôle
                                    </h4>
                                    <div className="space-y-2">
                                        {Object.entries(selectedYearData.roles).map(([role, count]) => {
                                            const pct = ((count / selectedYearData.total) * 100).toFixed(0);
                                            return (
                                                <div key={role} className="flex items-center justify-between text-xs gap-3">
                                                    <div className="flex items-center gap-2 w-28">
                                                        <div className={cn("w-2 h-2 rounded-full", getRoleColor(role))} />
                                                        <span className="text-slate-300 truncate">{role}</span>
                                                    </div>
                                                    <div className="flex-1 text-right text-slate-500 font-medium">
                                                        {pct}% ({count})
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Category Breakdown */}
                            {sortedCategories.length > 0 && (
                                <div className="space-y-2.5 pt-3 border-t border-white/5">
                                    <h4 className="text-xs font-semibold text-white flex items-center gap-1.5">
                                        <BookOpen size={13} className="text-slate-400" />
                                        Top Catégories Empruntées
                                    </h4>
                                    <div className="space-y-2">
                                        {sortedCategories.map(([category, count]) => {
                                            return (
                                                <div key={category} className="flex items-center justify-between text-xs gap-3">
                                                    <span className="text-slate-300 truncate w-36">{category}</span>
                                                    <div className="text-slate-400 font-semibold flex items-center gap-1">
                                                        <span>{count}</span>
                                                        <span className="text-[10px] text-slate-600 font-medium">prêts</span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="text-center py-6 text-slate-500 text-xs">
                            Sélectionnez une année pour afficher les détails.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
